import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { getCurrentUser } from "@/services/authService";

/**
 * Single shared source of truth for "the currently logged-in user's profile" on the client.
 *
 * Before this existed, HeaderRight.js (navbar avatar dropdown), StudentDashboardHeader.js and
 * AdminDashboardHeader.js (dashboard banner avatar) each ran their own independent
 * getProfile()/getCurrentUser() fetch into local component state -- so a successful profile
 * picture upload in Settings had no way to reach any of them without a full page reload.
 *
 * Every consumer now reads from this context instead, and Settings calls refreshProfile()
 * after a successful mutation (avatar upload, banner style change, etc.) so every subscriber
 * re-renders with the new data immediately.
 */
const ProfileCtx = createContext(null);

export function ProfileProvider({ children }) {
  const [profile, setProfile] = useState(null); // shape: the profile-datas payload (FIRSTNAME, DP, BANNER, USER_ROLE, ...)
  const [loading, setLoading] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);
  const initialized = useRef(false);
  const router = useRouter();

  const refreshProfile = useCallback(async () => {
    try {
      const res = await getCurrentUser(); // /api/auth/me -> { loggedIn, user }
      setLoggedIn(!!res.loggedIn);
      setProfile(res.loggedIn ? res.user : null);
      return res.loggedIn ? res.user : null;
    } catch (err) {
      setLoggedIn(false);
      setProfile(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Lets a component optimistically patch a field (e.g. DP right after upload) without waiting
  // on a full network round trip, while refreshProfile() still runs to reconcile with the server.
  const patchProfile = useCallback((patch) => {
    setProfile((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    refreshProfile();
  }, [refreshProfile]);

  // Belt-and-braces re-validation on every completed client-side navigation, on top of the
  // explicit refreshProfile() calls LoginForm.js/User.js already make right after login/logout.
  // Those explicit calls are correct in isolation, but this app renders a fresh HeaderContainer
  // per page (every page component builds its own), and a route change kicks off before React
  // necessarily finishes committing the auth state update from the action that triggered it --
  // this was showing up as the navbar occasionally rendering one navigation "behind" the real
  // auth state (still showing the old user's dropdown right after logout, or still showing
  // Log In/Join for Free right after login) depending on exactly how those two async operations
  // interleaved. Re-checking /api/auth/me after every route change closes that race for good,
  // regardless of the exact timing that triggers it -- it's a cheap, same-origin call (see
  // pages/api/auth/me.js), not worth trying to win a more surgical timing fix against.
  useEffect(() => {
    const onRouteChangeComplete = () => {
      refreshProfile();
    };
    router.events.on("routeChangeComplete", onRouteChangeComplete);
    return () => router.events.off("routeChangeComplete", onRouteChangeComplete);
  }, [router.events, refreshProfile]);

  return (
    <ProfileCtx.Provider value={{ profile, loading, loggedIn, refreshProfile, patchProfile }}>
      {children}
    </ProfileCtx.Provider>
  );
}

export function useProfile() {
  const ctx = useContext(ProfileCtx);
  if (!ctx) {
    // Defensive fallback rather than a hard throw -- some pages render outside the provider
    // tree (e.g. fully public pages that never mounted it). Behaves like "not logged in".
    return { profile: null, loading: false, loggedIn: false, refreshProfile: async () => null, patchProfile: () => {} };
  }
  return ctx;
}

export default ProfileProvider;
